import { Product } from '@diamond/shared/types';
import {
  getUniqueArray,
  LIST_FEATURE_FLAGS,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { useQuery } from '@tanstack/react-query';
import shallow from 'zustand/shallow';

import { getProductList } from '../queries';
import { useAuthStore, useShipToStore } from '../stores';
import {
  PRODUCT_RECOMMENDATIONS_KEY,
  useProductRecommendations,
} from './useProductRecommendations';

type ProductAutocompleteSource = 'recommendation' | 'fallback' | 'none';

type ProductAutocompleteResult = {
  products: string[];
  brands: string[];
  source: ProductAutocompleteSource;
  isLoading: boolean;
  isEmpty: boolean;
};

const FALLBACK_FLAG_KEY = LIST_FEATURE_FLAGS.FF_PRODUCT_AUTOCOMPLETE_FALLBACK;
const DEFAULT_FALLBACK_SIZE = 10;

export const PRODUCT_AUTOCOMPLETE_KEY = 'ProductAutocomplete';

export const useProductAutocomplete = (
  keyword: string,
  options?: {
    enableFallback?: boolean;
    size?: number;
  }
): ProductAutocompleteResult => {
  const session = useAuthStore();
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );

  const trimmedKeyword = keyword.trim();
  const fallbackFlag = useFeatureFlag(FALLBACK_FLAG_KEY, true);
  const fallbackEnabled = options?.enableFallback ?? fallbackFlag;
  const fallbackSize = options?.size ?? DEFAULT_FALLBACK_SIZE;

  const recommendationQuery = useProductRecommendations(trimmedKeyword);

  const isRecommendationEmpty = Boolean(
    recommendationQuery.data &&
      recommendationQuery.data.products.length === 0 &&
      recommendationQuery.data.brands.length === 0
  );

  const hasRecommendationData =
    Boolean(recommendationQuery.data) &&
    (recommendationQuery.data.products.length > 0 ||
      recommendationQuery.data.brands.length > 0);

  const shouldFetchFallback =
    fallbackEnabled &&
    trimmedKeyword !== '' &&
    recommendationQuery.isSuccess &&
    isRecommendationEmpty &&
    !hasRecommendationData;

  const fallbackQuery = useQuery<Product[]>({
    queryKey: [
      PRODUCT_AUTOCOMPLETE_KEY,
      'fallback',
      {
        keyword: trimmedKeyword,
        warehouse: selectedShipToId,
        size: fallbackSize,
      },
    ],
    queryFn: () =>
      getProductList(session.access_token, {
        search_query: trimmedKeyword,
        warehouse: selectedShipToId,
        page: 1,
        size: fallbackSize,
      }),
    enabled: shouldFetchFallback,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const fallbackProducts = getUniqueArray(
    (fallbackQuery.data ?? [])
      .map((product) => product.title)
      .filter((title): title is string => Boolean(title))
  );

  const fallbackBrands = getUniqueArray(
    (fallbackQuery.data ?? [])
      .map((product) => product.brand_desc || product.brand_name)
      .filter((brand): brand is string => Boolean(brand))
  );

  const hasFallbackData =
    fallbackProducts.length > 0 || fallbackBrands.length > 0;

  const products = hasRecommendationData
    ? getUniqueArray(recommendationQuery.data?.products ?? [])
    : hasFallbackData
    ? fallbackProducts
    : [];

  const brands = hasRecommendationData
    ? getUniqueArray(recommendationQuery.data?.brands ?? [])
    : hasFallbackData
    ? fallbackBrands
    : [];

  let source: ProductAutocompleteSource = 'none';
  if (hasRecommendationData) {
    source = 'recommendation';
  } else if (hasFallbackData) {
    source = 'fallback';
  }

  const isLoadingPrimary = recommendationQuery.isPending;
  const isLoadingFallback = shouldFetchFallback && fallbackQuery.isPending;
  const isFetchingWithoutData =
    (!hasRecommendationData && recommendationQuery.isFetching) ||
    (!hasFallbackData && fallbackQuery.isFetching);

  const isLoading =
    isLoadingPrimary || isLoadingFallback || isFetchingWithoutData;

  const isEmpty =
    !isLoading &&
    !hasRecommendationData &&
    !hasFallbackData &&
    trimmedKeyword !== '';

  return {
    products,
    brands,
    source,
    isLoading,
    isEmpty,
  };
};
